import React, { useState, useEffect } from 'react';
import {
  notificationService,
  Notification,
  NotificationStats,
  CreateNotificationData,
  NotificationFilters,
} from '../services/notificationService';
import { notificationTemplateService, NotificationTemplate } from '../services/notificationTemplateService';

const NotificationManagement: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NotificationFilters>({
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);

  useEffect(() => {
    loadNotifications();
    loadStats();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications(filters);
      setNotifications(data.notifications);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await notificationService.getNotificationStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await notificationTemplateService.getTemplates({ limit: 100 });
      setTemplates(data.templates);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const handleFilterChange = (newFilters: Partial<NotificationFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleSendNotification = async (id: string) => {
    try {
      await notificationService.sendNotification(id);
      await loadNotifications();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification');
    }
  };

  const handleCancelNotification = async (id: string) => {
    try {
      await notificationService.cancelNotification(id);
      await loadNotifications();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel notification');
    }
  };

  const handleDeleteNotification = async (id: string) => {
    if (!window.confirm('정말로 이 알림을 삭제하시겠습니까?')) return;

    try {
      await notificationService.deleteNotification(id);
      await loadNotifications();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete notification');
    }
  };

  const handleViewDetails = async (id: string) => {
    try {
      const notification = await notificationService.getNotification(id);
      setSelectedNotification(notification);
      setShowDetailsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notification details');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'sending':
        return 'bg-yellow-100 text-yellow-800';
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'basic':
        return 'bg-blue-100 text-blue-800';
      case 'promotion':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'basic':
        return '📱 기본 알림';
      case 'promotion':
        return '🎁 프로모션 알림';
      default:
        return type;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      // 기본 알림 카테고리
      case 'service_maintenance':
        return '서비스 점검/장애';
      case 'security_alert':
        return '보안 알림';
      case 'policy_change':
        return '정책 변경';
      case 'analysis_complete':
        return '분석 완료';
      case 'service_update':
        return '서비스 업데이트';
      // 프로모션 알림 카테고리
      case 'event_discount':
        return '이벤트/할인';
      case 'new_feature':
        return '신규 기능';
      case 'special_benefit':
        return '특별 혜택';
      default:
        return category;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return '초안';
      case 'scheduled':
        return '예약됨';
      case 'sending':
        return '발송중';
      case 'sent':
        return '발송완료';
      case 'failed':
        return '발송실패';
      case 'cancelled':
        return '취소됨';
      default:
        return status;
    }
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">알림 관리</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTemplateModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            템플릿 발송
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            새 알림 작성
          </button>
        </div>
      </div>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 1 0-15 0v5h5l-5 5-5-5h5v-5a7.5 7.5 0 1 1 15 0v5z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 알림</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.overview.totalNotifications}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">발송 완료</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.overview.totalSent}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">전달됨</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.overview.totalDelivered}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">열림</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.overview.totalOpened}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">알림 유형</label>
            <select
              value={filters.type || ''}
              onChange={(e) => handleFilterChange({ type: e.target.value || undefined })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              <option value="basic">📱 기본 알림</option>
              <option value="promotion">🎁 프로모션 알림</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange({ status: e.target.value || undefined })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              <option value="draft">초안</option>
              <option value="scheduled">예약됨</option>
              <option value="sending">발송중</option>
              <option value="sent">발송완료</option>
              <option value="failed">발송실패</option>
              <option value="cancelled">취소됨</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">페이지당 표시</label>
            <select
              value={filters.limit || 20}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10개</option>
              <option value={20}>20개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
            </select>
          </div>
        </div>
      </div>

      {/* 알림 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">제목</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">유형</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  카테고리
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">대상</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">통계</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  생성일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notifications.map((notification) => (
                <tr key={notification.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{notification.title}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">{notification.body}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(notification.type)}`}
                    >
                      {getTypeLabel(notification.type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{getCategoryLabel(notification.category)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}
                    >
                      {getStatusLabel(notification.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {notification.targetUsers.type === 'all' && '전체 사용자'}
                    {notification.targetUsers.type === 'specific' &&
                      `${notification.targetUsers.userIds?.length || 0}명`}
                    {notification.targetUsers.type === 'segment' && notification.targetUsers.segment}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="space-y-1">
                      <div>발송: {notification.stats.totalSent}</div>
                      <div>전달: {notification.stats.totalDelivered}</div>
                      <div>열림: {notification.stats.totalOpened}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(notification.createdAt).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleViewDetails(notification.id)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      상세
                    </button>
                    {notification.status === 'draft' && (
                      <button
                        onClick={() => handleSendNotification(notification.id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        발송
                      </button>
                    )}
                    {(notification.status === 'draft' || notification.status === 'scheduled') && (
                      <button
                        onClick={() => handleCancelNotification(notification.id)}
                        className="text-yellow-600 hover:text-yellow-900"
                      >
                        취소
                      </button>
                    )}
                    {(notification.status === 'draft' || notification.status === 'cancelled') && (
                      <button
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        삭제
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  총 <span className="font-medium">{pagination.total}</span>개 중{' '}
                  <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>-
                  <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                  개 표시
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    이전
                  </button>
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    다음
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 알림 생성 모달 */}
      {showCreateModal && (
        <CreateNotificationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadNotifications();
            loadStats();
          }}
        />
      )}

      {/* 알림 상세 모달 */}
      {showDetailsModal && selectedNotification && (
        <NotificationDetailsModal
          notification={selectedNotification}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedNotification(null);
          }}
        />
      )}

      {/* 템플릿 발송 모달 */}
      {showTemplateModal && (
        <TemplateSendModal
          templates={templates}
          onClose={() => setShowTemplateModal(false)}
          onSuccess={() => {
            setShowTemplateModal(false);
            loadNotifications();
            loadStats();
          }}
        />
      )}
    </div>
  );
};

// 알림 생성 모달 컴포넌트
const CreateNotificationModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateNotificationData>({
    title: '',
    body: '',
    type: 'system',
    category: 'system',
    targetUsers: {
      type: 'all',
    },
    data: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await notificationService.createNotification(formData);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">새 알림 작성</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">제목</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">내용</label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">유형</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      type: e.target.value as any,
                      category: e.target.value as any,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="basic">📱 기본 알림</option>
                  <option value="promotion">🎁 프로모션 알림</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">대상</label>
                <select
                  value={formData.targetUsers.type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      targetUsers: {
                        ...prev.targetUsers,
                        type: e.target.value as any,
                      },
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">전체 사용자</option>
                  <option value="segment">세그먼트</option>
                </select>
              </div>
            </div>

            {formData.targetUsers.type === 'segment' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">세그먼트</label>
                <select
                  value={formData.targetUsers.segment || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      targetUsers: {
                        ...prev.targetUsers,
                        segment: e.target.value as any,
                      },
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="premium">프리미엄 사용자</option>
                  <option value="free">무료 사용자</option>
                  <option value="active">활성 사용자</option>
                  <option value="inactive">비활성 사용자</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">예약 발송 (선택사항)</label>
              <input
                type="datetime-local"
                value={formData.scheduledAt || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, scheduledAt: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '생성 중...' : '생성'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 알림 상세 모달 컴포넌트
const NotificationDetailsModal: React.FC<{
  notification: Notification;
  onClose: () => void;
}> = ({ notification, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">알림 상세 정보</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">제목</label>
              <p className="mt-1 text-sm text-gray-900">{notification.title}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">내용</label>
              <p className="mt-1 text-sm text-gray-900">{notification.body}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">유형</label>
                <p className="mt-1 text-sm text-gray-900">{notification.type}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">상태</label>
                <p className="mt-1 text-sm text-gray-900">{notification.status}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">대상</label>
                <p className="mt-1 text-sm text-gray-900">
                  {notification.targetUsers.type === 'all' && '전체 사용자'}
                  {notification.targetUsers.type === 'specific' && `${notification.targetUsers.userIds?.length || 0}명`}
                  {notification.targetUsers.type === 'segment' && notification.targetUsers.segment}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">생성자</label>
                <p className="mt-1 text-sm text-gray-900">{notification.createdBy.nickname}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">발송 통계</label>
              <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-semibold text-blue-600">{notification.stats.totalSent}</p>
                  <p className="text-sm text-gray-500">발송</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-green-600">{notification.stats.totalDelivered}</p>
                  <p className="text-sm text-gray-500">전달</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-purple-600">{notification.stats.totalOpened}</p>
                  <p className="text-sm text-gray-500">열림</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-red-600">{notification.stats.totalFailed}</p>
                  <p className="text-sm text-gray-500">실패</p>
                </div>
              </div>
            </div>

            {notification.deliveryLog && notification.deliveryLog.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700">발송 로그</label>
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          사용자 ID
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          발송 시간
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          메시지 ID
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          토큰 수
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          오류
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {notification.deliveryLog.map((log, index) => (
                        <tr key={index} className={log.status === 'sent' ? 'bg-green-50' : 'bg-red-50'}>
                          <td className="px-3 py-2 text-sm text-gray-900">{log.userId}</td>
                          <td className="px-3 py-2 text-sm">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                log.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {log.status === 'sent' ? '성공' : '실패'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {new Date(log.sentAt).toLocaleString('ko-KR')}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {log.messageIds && log.messageIds.length > 0 ? (
                              <div className="space-y-1">
                                {log.messageIds.map((messageId, idx) => (
                                  <div key={idx} className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                                    {messageId}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">{log.tokenCount || 0}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {log.errorMessage ? (
                              <span className="text-red-600 text-xs">{log.errorMessage}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {notification.scheduledAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700">예약 발송 시간</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(notification.scheduledAt).toLocaleString('ko-KR')}
                </p>
              </div>
            )}

            {notification.sentAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700">발송 시간</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(notification.sentAt).toLocaleString('ko-KR')}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">생성 시간</label>
              <p className="mt-1 text-sm text-gray-900">{new Date(notification.createdAt).toLocaleString('ko-KR')}</p>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 템플릿 발송 모달 컴포넌트
const TemplateSendModal: React.FC<{
  templates: NotificationTemplate[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ templates, onClose, onSuccess }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [formData, setFormData] = useState<CreateNotificationData>({
    title: '',
    body: '',
    type: 'system',
    category: 'system',
    targetUsers: {
      type: 'all',
    },
    data: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTemplateSelect = (template: NotificationTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      body: template.body,
      type: template.type,
      category: template.type,
      targetUsers: {
        type: 'all',
      },
      data: template.data || {},
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // 알림 생성 및 즉시 발송
      const notification = await notificationService.createNotification(formData);
      await notificationService.sendNotification(notification.id);

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">템플릿으로 즉시 발송</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 템플릿 선택 */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">템플릿 선택</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedTemplate?.id === template.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="font-medium text-gray-900">{template.name}</h5>
                        <p className="text-sm text-gray-600 mt-1">{template.title}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.body}</p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          template.type === 'analysis_complete'
                            ? 'bg-purple-100 text-purple-800'
                            : template.type === 'system'
                              ? 'bg-blue-100 text-blue-800'
                              : template.type === 'promotion'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-orange-100 text-orange-800'
                        }`}
                      >
                        {template.type === 'analysis_complete'
                          ? '분석완료'
                          : template.type === 'system'
                            ? '시스템'
                            : template.type === 'promotion'
                              ? '프로모션'
                              : '마케팅'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 발송 설정 */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">발송 설정</h4>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">제목</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">내용</label>
                  <textarea
                    value={formData.body}
                    onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">유형</label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          type: e.target.value as any,
                          category: e.target.value as any,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="basic">📱 기본 알림</option>
                      <option value="promotion">🎁 프로모션 알림</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">대상</label>
                    <select
                      value={formData.targetUsers.type}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          targetUsers: {
                            ...prev.targetUsers,
                            type: e.target.value as any,
                          },
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">전체 사용자</option>
                      <option value="segment">세그먼트</option>
                    </select>
                  </div>
                </div>

                {formData.targetUsers.type === 'segment' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">세그먼트</label>
                    <select
                      value={formData.targetUsers.segment || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          targetUsers: {
                            ...prev.targetUsers,
                            segment: e.target.value as any,
                          },
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="premium">프리미엄 사용자</option>
                      <option value="free">무료 사용자</option>
                      <option value="active">활성 사용자</option>
                      <option value="inactive">비활성 사용자</option>
                    </select>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !selectedTemplate}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? '발송 중...' : '즉시 발송'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationManagement;
